import { useMemo, useState, useRef, useEffect } from "react";
import { toast } from "sonner";
import {
  Sparkles, PlayCircle, MoonStar, ExternalLink, Coffee,
  Calendar, Clock, ChevronDown,
} from "lucide-react";
import { useHomeOSState } from "../state/HomeOSStateProvider";
import {
  getRenewalsReadyToPrep, getRenewalPrepSummary, buildRenewalPrepTask,
  buildSnoozePatch,
} from "../utils/documentUtils";

/* ════════════════════════════════════════════════════════════════════════
   RENEWAL PREP CARD — "ARA started prepping…" nudges.
   Variants:
     - "today": full card with up to 3 nudge rows + Start / Snooze / Open
     - "wall" : single "Start soon: <title>" line
     - "list" : list-only inline use (e.g. inside RenewalRadar full view)
   ════════════════════════════════════════════════════════════════════════ */

const SNOOZE_OPTIONS = [
  { id: "tomorrow", label: "Tomorrow",   days: 1 },
  { id: "3days",    label: "In 3 days",  days: 3 },
  { id: "week",     label: "Next week",  days: 7 },
];

export function RenewalPrepCard({
  variant = "today",
  onOpenDocument,
  limit = 3,
}) {
  const { documents, tasks, addTask, updateDocument } = useHomeOSState();

  const nudges = useMemo(
    () => getRenewalsReadyToPrep(documents || [], tasks || [], { limit }),
    [documents, tasks, limit]
  );
  const summary = useMemo(
    () => getRenewalPrepSummary(documents || [], tasks || []),
    [documents, tasks]
  );

  const startTask = (nudge) => {
    const payload = buildRenewalPrepTask(nudge.doc);
    const taskId = addTask(payload);
    updateDocument(nudge.doc.id, {
      linkedTaskId: taskId,
      status: nudge.doc.status === "Archived" ? "Archived" : "Reminder set",
      prepStartedAt: new Date().toISOString(),
    });
    toast.success("ARA added this to Today.");
  };

  const snooze = (nudge, days, label) => {
    updateDocument(nudge.doc.id, buildSnoozePatch(days));
    toast(`Snoozed until ${label.toLowerCase()}.`);
  };

  /* ── Wall variant ──────────────────────────────────────────── */
  if (variant === "wall") {
    if (!summary.next) return null;
    const n = summary.next;
    return (
      <div className="mt-3 pt-3 border-t border-[color:var(--ara-line-soft)] dark:border-violet-700/50 flex items-center gap-2" data-testid="renewal-prep-wall">
        <div className="w-7 h-7 rounded-xl    flex items-center justify-center flex-shrink-0">
          <Sparkles className="w-3.5 h-3.5 text-white" strokeWidth={1.5} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-extrabold uppercase tracking-wider text-fuchsia-700 dark:text-fuchsia-300">ARA started prepping</p>
          <p className="text-base font-extrabold text-gray-900 dark:text-gray-50 truncate">Start soon: {n.doc.title}</p>
        </div>
        {onOpenDocument && (
          <button
            onClick={() => onOpenDocument(n.doc)}
            className="text-[11px] font-extrabold text-[color:var(--ara-ink-muted)] dark:text-[color:var(--ara-ink-muted)] hover:underline flex-shrink-0"
            data-testid="renewal-prep-wall-open"
          >
            Open
          </button>
        )}
      </div>
    );
  }

  /* ── Today variant (full card) ─────────────────────────────── */
  if (variant === "today") {
    if (nudges.length === 0) {
      return (
        <div className="rounded-2xl border border-gray-100 dark:border-slate-700 bg-white dark:bg-slate-800/80 p-4" data-testid="renewal-prep-today-empty">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-2xl    flex items-center justify-center shadow-sm">
              <Coffee className="w-4 h-4 text-white" strokeWidth={1.5} />
            </div>
            <p className="text-sm font-extrabold text-gray-700 dark:text-gray-200">Nothing needs prepping today.</p>
          </div>
        </div>
      );
    }

    return (
      <section className="rounded-[28px]     dark: dark: dark: border border-[color:var(--ara-line-soft)] dark:border-fuchsia-800/60 p-5" data-testid="renewal-prep-today">
        <div className="flex items-center gap-3 mb-3 flex-wrap">
          <div className="w-11 h-11 rounded-2xl    flex items-center justify-center shadow-sm">
            <Sparkles className="w-5 h-5 text-white" strokeWidth={1.5} />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[11px] font-extrabold uppercase tracking-wider text-fuchsia-700 dark:text-fuchsia-300">ARA started prepping…</p>
            <h2 className="text-xl sm:text-2xl font-extrabold text-gray-900 dark:text-gray-50 leading-tight">
              {nudges.length} thing{nudges.length === 1 ? "" : "s"} to gently get ahead of
            </h2>
          </div>
        </div>

        <ul className="space-y-2.5" data-testid="renewal-prep-today-list">
          {nudges.map(n => (
            <RenewalPrepRow
              key={n.id}
              nudge={n}
              onStart={() => startTask(n)}
              onSnooze={(days, label) => snooze(n, days, label)}
              onOpen={() => onOpenDocument?.(n.doc)}
            />
          ))}
        </ul>
      </section>
    );
  }

  /* ── List variant (e.g. inline in RenewalRadar full) ───────── */
  return (
    <ul className="space-y-2" data-testid="renewal-prep-list">
      {nudges.map(n => (
        <RenewalPrepRow
          key={n.id}
          nudge={n}
          onStart={() => startTask(n)}
          onSnooze={(days, label) => snooze(n, days, label)}
          onOpen={() => onOpenDocument?.(n.doc)}
        />
      ))}
    </ul>
  );
}

function RenewalPrepRow({ nudge, onStart, onSnooze, onOpen }) {
  const [snoozeOpen, setSnoozeOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!snoozeOpen) return;
    const handler = (e) => {
      if (!menuRef.current?.contains(e.target)) setSnoozeOpen(false);
    };
    window.document.addEventListener("mousedown", handler);
    return () => window.document.removeEventListener("mousedown", handler);
  }, [snoozeOpen]);

  return (
    <li
      className="rounded-2xl bg-white/85 dark:bg-slate-800/60 border border-[color:var(--ara-line-soft)] dark:border-fuchsia-800/40 p-3.5"
      data-testid={`renewal-prep-row-${nudge.doc.id}`}
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-2xl    dark: dark: flex items-center justify-center flex-shrink-0">
          <Sparkles className="w-4 h-4 text-fuchsia-600 dark:text-fuchsia-300" strokeWidth={1.5} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-extrabold text-gray-900 dark:text-gray-50 leading-tight">{nudge.doc.title}</p>
          <p className="text-[12px] font-medium text-gray-600 dark:text-gray-300 leading-snug mt-0.5">{nudge.message}</p>
          <p className="text-[11px] font-extrabold text-fuchsia-700 dark:text-fuchsia-300 mt-1 flex items-center gap-1">
            <PlayCircle className="w-3 h-3" strokeWidth={1.5} /> Suggested: {nudge.action}
          </p>
        </div>
      </div>
      <div className="mt-2.5 flex items-center gap-1.5 flex-wrap">
        <button
          onClick={onStart}
          className="inline-flex items-center gap-1    hover: hover: text-white font-extrabold text-xs px-3 py-1.5 rounded-full shadow-sm hover:shadow-md active:scale-[0.97]"
          data-testid={`renewal-prep-start-${nudge.doc.id}`}
        >
          <PlayCircle className="w-3.5 h-3.5" strokeWidth={1.5} /> Start task
        </button>
        <div className="relative" ref={menuRef}>
          <button
            onClick={() => setSnoozeOpen(v => !v)}
            className="inline-flex items-center gap-1 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 text-gray-700 dark:text-gray-200 font-extrabold text-xs px-3 py-1.5 rounded-full"
            data-testid={`renewal-prep-snooze-${nudge.doc.id}`}
          >
            <MoonStar className="w-3.5 h-3.5" strokeWidth={1.5} /> Snooze
            <ChevronDown className="w-3 h-3" strokeWidth={1.5} />
          </button>
          {snoozeOpen && (
            <div className="absolute z-30 mt-1.5 left-0 w-44 rounded-2xl bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 shadow-none overflow-hidden" data-testid={`renewal-prep-snooze-menu-${nudge.doc.id}`}>
              {SNOOZE_OPTIONS.map(opt => (
                <button
                  key={opt.id}
                  onClick={() => { setSnoozeOpen(false); onSnooze(opt.days, opt.label); }}
                  className="w-full text-left px-3 py-2 text-xs font-bold text-gray-700 dark:text-gray-200 hover:bg-transparent dark:hover:bg-fuchsia-900/20 flex items-center gap-2"
                  data-testid={`renewal-prep-snooze-${nudge.doc.id}-${opt.id}`}
                >
                  {opt.id === "tomorrow" ? <Clock className="w-3.5 h-3.5" strokeWidth={1.5} /> : <Calendar className="w-3.5 h-3.5" strokeWidth={1.5} />}
                  {opt.label}
                </button>
              ))}
            </div>
          )}
        </div>
        {onOpen && (
          <button
            onClick={onOpen}
            className="ml-auto inline-flex items-center gap-1 text-xs font-extrabold text-[color:var(--ara-ink-muted)] dark:text-[color:var(--ara-ink-muted)] hover:underline"
            data-testid={`renewal-prep-open-${nudge.doc.id}`}
          >
            Open document <ExternalLink className="w-3 h-3" strokeWidth={1.5} />
          </button>
        )}
      </div>
    </li>
  );
}

export default RenewalPrepCard;
